import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import ThemeToggle from "./ThemeToggle";

const Navbar: React.FC = () => {
  const { user } = useAuth();
  return (
    <nav className="relative w-full flex items-center justify-between px-4 sm:px-8 py-3 sm:py-4 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-lg border-b border-gray-200/50 dark:border-gray-700/50">
      {/* Subtle gradient overlay for luxury effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-pink-500/2 via-purple-500/2 to-indigo-500/2 pointer-events-none" />

      {/* Left: Compact Navbar Logo - Visible on all screen sizes */}
      <div className="relative flex items-center group">
        {/* Light mode navbar logo - Visible on all screen sizes */}
        <img
          src="/logos/stylistgpt-logo.svg"
          alt="StylistGPT"
          className="h-8 sm:h-10 w-auto dark:hidden hover:scale-110 transition-all duration-500 filter drop-shadow-md group-hover:drop-shadow-lg"
        />
        {/* Dark mode navbar logo - Visible on all screen sizes */}
        <img
          src="/logos/stylistgpt-logo.svg"
          alt="StylistGPT"
          className="h-8 sm:h-10 w-auto hidden dark:block hover:scale-110 transition-all duration-500 filter drop-shadow-md group-hover:drop-shadow-lg"
        />

        {/* Subtle glow effect on logo hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-indigo-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl" />
      </div>

      {/* Right: Elegant Theme Toggle & Couture Auth Links */}
      <div className="relative flex gap-6 items-center">
        {/* Decorative gradient line */}
        <div className="absolute -top-2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent opacity-30" />

        <div className="relative">
          <ThemeToggle />
        </div>

        {user ? (
          <div className="flex gap-8 items-center">
            <Link
              to="/home"
              className="group relative px-4 py-2 text-sm font-light tracking-wider uppercase text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-all duration-300 rounded-lg hover:bg-gray-50/50 dark:hover:bg-gray-800/50"
            >
              <span className="relative z-10">Home</span>
              <div className="absolute inset-x-0 -bottom-1 h-px bg-gradient-to-r from-transparent via-pink-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {/* Subtle background glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/5 via-purple-500/5 to-indigo-500/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Link>
          </div>
        ) : (
          <div className="gap-3 sm:gap-6 items-center hidden">
            <Link
              to="/login"
              className="group relative px-3 sm:px-4 py-2 text-xs sm:text-sm font-light tracking-wider uppercase text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-all duration-300 hover:bg-gray-50/50 dark:hover:bg-gray-800/50"
            >
              <span className="relative z-10">Login</span>
              <div className="absolute inset-x-0 -bottom-1 h-px bg-gradient-to-r from-transparent via-gray-400 dark:via-gray-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {/* Subtle background glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-gray-500/5 via-gray-400/5 to-gray-500/5 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Link>

            <Link
              to="/register"
              className="group relative px-4 sm:px-8 py-2 sm:py-3 text-xs sm:text-sm font-light tracking-wider uppercase text-white bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 dark:from-gray-200 dark:via-gray-100 dark:to-gray-200 dark:text-gray-900 hover:shadow-xl hover:scale-105 transition-all duration-300 border border-gray-600/50 dark:border-gray-300/50 backdrop-blur-sm"
            >
              <span className="relative z-10">Register</span>

              {/* Enhanced gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-gray-700 via-gray-600 to-gray-700 dark:from-gray-100 dark:via-white dark:to-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg" />

              {/* Subtle inner glow */}
              <div className="absolute inset-0.5 bg-gradient-to-r from-pink-500/10 via-purple-500/10 to-indigo-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

              {/* Elegant shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 rounded-lg" />
            </Link>
          </div>
        )}

        {/* Decorative gradient line */}
        <div className="absolute -bottom-2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent opacity-30" />
      </div>
    </nav>
  );
};

export default Navbar;
